import type { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import type { SceneEnvironmentPatch, SceneInfo, SceneViewState } from "@models/data/sceneInfo";

/**
 * `ISceneModel` — port of Unity's `ISceneModel` / `BaseSceneModel`
 * (`Models/SceneModel/ISceneModel.cs`, `BaseSceneModel.cs`). Owns the
 * active-scene state that the runtime cares about: which scene is live,
 * its origin transform, and the scene environment (table / console /
 * grid / shadows / default lights).
 *
 * Skybox state lives on `ISkyboxModel` (`SkyboxModel.ts`), matching the
 * Unity split. Everything else from `BaseSceneModel` (XR, dollhouse,
 * isolation, scene mode transitions) is deferred — it surfaces in later
 * milestones once we have the matching tools.
 *
 * Subscribes to `IProjectModel.onActiveSceneChanged` so consumers don't
 * have to choose between the two — composables can rely solely on
 * `ISceneModel`.
 */
/**
 * A camera pose as the scene view models it: a world position plus the world
 * point the camera orbits. **UNITY axes, metres** — the same space the scene
 * row persists, so nothing here needs converting before it is written.
 *
 * Deliberately no rotation: this camera is an orbit rig on both clients
 * (Battlehub's `CameraPosition` setter re-aims at `Pivot`; the web's
 * `OrbitCameraController` derives the transform from target + alpha/beta/
 * radius), so the pair fully determines the view and the zoom is `|position -
 * pivot|`.
 */
export interface SceneCameraPose {
  readonly position: Vec3;
  readonly pivot: Vec3;
}

/**
 * The seam between `ISceneModel` — which decides WHICH pose and WHEN — and the
 * viewport, which owns the Three.js camera. The inverse-direction sibling of
 * `IPreviewCaptureHost`: `src/models/**` may not import `three`, but the scene
 * model still owns the scene-view lifecycle, exactly as Unity's
 * `DesktopSceneModel` does.
 *
 * Registered by `ViewportView` on mount and cleared on unmount; every method
 * on `ISceneModel` that needs it no-ops when nothing is attached, so a
 * headless run (or a scene change while the viewport is remounting) is safe.
 * Note this is why the model does NOT reach for `Ioc.isRegistered(ORBIT_CAMERA)`
 * — that token is never unregistered, so it stays true for a disposed camera.
 *
 * All poses crossing this boundary are UNITY space; the host converts.
 */
export interface ISceneViewCameraHost {
  /**
   * The live camera pose. Unity: `m_sceneComponent.CameraPosition` /
   * `.Pivot`.
   */
  getCameraPose(): SceneCameraPose | null;
  /**
   * The camera's own default pose, used when a scene has no authored view.
   * Unity's `m_initialCameraPosition` / `m_initialPivot`, seeded in `Start()`
   * from the live component before any scene loads.
   */
  getDefaultPose(): SceneCameraPose;
  /**
   * Move the camera. `durationSeconds <= 0` snaps (Unity `ApplyCameraState`);
   * anything else animates (Unity `StartCameraTransition`).
   */
  applyCameraPose(pose: SceneCameraPose, durationSeconds: number): void;
  /**
   * True while a camera animation is in flight — the web spelling of Unity's
   * `m_cameraResetCoroutine != null` guard in `OnLoadSceneCompleted`.
   */
  readonly isCameraTransitionActive: boolean;
  /**
   * Where the pose move in flight will land, or null when none is (a focus
   * glide is not a pose move and answers null). Lets
   * {@link ISceneModel.frameActiveSceneView} tell "already on its way there"
   * from "moving somewhere else". Optional: a host without it just gets the
   * move re-issued, which redirects smoothly from the live pose.
   */
  getPoseTransitionDestination?(): SceneCameraPose | null;
}

export interface ISceneModel {
  readonly onActiveSceneChanged: Emitter<SceneInfo | null>;
  /** Forward of {@link IProjectModelSlim.onBeginLoadScene} (same payload). */
  readonly onBeginLoadScene: Emitter<{
    sceneId: string;
    fromSceneId: string | null;
    isSmoothTransition: boolean;
    showProgressUi: boolean;
  }>;
  readonly onLoadSceneCompleted: Emitter<{ sceneId: string }>;
  /**
   * Port of Unity `ISceneModel.SceneEnvironmentChanged` — fired when the
   * five environment toggles change, before the cloud write settles.
   */
  readonly onSceneEnvironmentChanged: Emitter<SceneEnvironmentPatch>;

  readonly activeScene: SceneInfo | null;
  /** World-origin offset applied to the scene root in the viewport. */
  readonly originTransform: {
    readonly position: readonly [number, number, number];
    readonly rotation: readonly [number, number, number];
  };

  // Environment reads (Unity `BaseSceneModel.TableVisible` etc. — derived
  // from the active scene, defaulting to the Unity fresh-scene values).
  readonly tableVisible: boolean;
  readonly consoleVisible: boolean;
  readonly gridVisible: boolean;
  readonly shadowsVisible: boolean;
  readonly lightVisible: boolean;

  setOriginTransform(
    position: readonly [number, number, number],
    rotation: readonly [number, number, number],
  ): void;

  /**
   * Port of `BaseSceneModel.UpdateSceneEnvironmentAsync`
   * (`BaseSceneModel.cs:1276`): no-ops when nothing changed, emits
   * {@link onSceneEnvironmentChanged}, then persists through
   * `IProjectModel.updateSceneEnvironmentAsync`.
   */
  updateSceneEnvironmentAsync(env: SceneEnvironmentPatch): Promise<void>;

  /**
   * Port of `BaseSceneModel.SetDefaultEnvironmentAsync`
   * (`BaseSceneModel.cs:1299`): re-derives table visibility after a skybox
   * mode change (`tableVisible = skybox.mode === None`).
   */
  setDefaultEnvironmentAsync(): Promise<void>;

  // -- Scene view (port of `DesktopSceneModel`'s camera half:
  //    `SetSceneView` / `GetSceneView` / `SetSceneCamera` / `ResetCamera` /
  //    `TransitionResetCamera`, `DesktopSceneModel.cs:206-425`) --

  /**
   * Fired once a scene view has been applied AND its camera move has been
   * given the 0.1 s Unity waits before finishing — the port of
   * `DesktopSceneModel.FinishSceneView` (`:326`). Unity raises the
   * "Scene view set" toast from inside the model; the web keeps toasts in the
   * ViewModel layer, so the model announces the moment instead.
   *
   * Fires for UNDO too, because Unity's undo re-runs the whole sequence
   * rather than silently restoring state.
   */
  readonly onSceneViewApplied: Emitter<{ sceneId: string }>;

  /** Register the viewport's camera seam (`null` on unmount). */
  attachSceneViewCameraHost(host: ISceneViewCameraHost | null): void;

  /**
   * Whether a viewport has a camera attached. Every scene-view method no-ops
   * without one, so a caller that must REPORT what happened — the
   * `reset_scene_view` agent tool, which upstream answers with
   * `"error: scene model not available"` rather than a false ok — has to be
   * able to ask.
   *
   * Deliberately not `Ioc.isRegistered(SCENE_MODEL)`: the scene model is
   * registered unconditionally at bootstrap, so that test is a tautology.
   * (`ORBIT_CAMERA` is no better — it is registered on the first viewport
   * mount and never unregistered, so it stays true for a disposed camera.)
   */
  readonly isSceneViewCameraAttached: boolean;

  /**
   * The active scene's authored view, or `UNSET_SCENE_VIEW` when there is no
   * active scene.
   */
  readonly activeSceneView: SceneViewState;

  /**
   * The scene view the CURRENT camera would be saved as, without saving it —
   * the split-out first half of Unity's `SetSceneView()`, which builds its
   * `newState` tuple from the live component before handing it to
   * `UndoRedo.Record` (`DesktopSceneModel.cs:294-299`).
   *
   * Exposed so a caller can record an undo command whose redo APPLIES this
   * value, rather than re-sampling the camera on every redo — by then the
   * camera has moved and a redo would save a different view than the one it
   * is replaying. `null` when there is no active scene or no camera attached.
   */
  captureSceneView(): SceneViewState | null;

  /**
   * "Set Scene View" — save the CURRENT camera as the active scene's view.
   * Port of `DesktopSceneModel.SetSceneView()` (`:290`).
   *
   * Undo is NOT recorded here: Unity records it around this exact call, and
   * the web port keeps scene-level undo in the ViewModel layer (the
   * cutting-plane pose and the skybox colour do the same). Callers that want
   * it record a command whose two directions both run
   * {@link applySceneViewAsync}.
   */
  setSceneViewAsync(): Promise<void>;

  /**
   * Apply an explicit scene view — the port of Unity's private
   * `SetSceneView(GameObject, (position, pivot, isOrthographic))` overload
   * (`:307`), which is what its undo/redo delegates both call.
   *
   * Persists, moves the camera over 0.1 s, and raises
   * {@link onSceneViewApplied} once that lands.
   */
  applySceneViewAsync(sceneId: string, view: SceneViewState): Promise<void>;

  /**
   * Fly the camera to an explicit pose and persist NOTHING — no scene row, no
   * thumbnail refresh, no `update_scene` broadcast, no toast. The move-only
   * half of {@link applySceneViewAsync}, for a caller that owns a framing of
   * its own: today the workflow run's per-step `step_view`.
   *
   * `durationSeconds <= 0` snaps; anything else animates, exactly as the scene
   * change's own move does.
   *
   * Returns false when no viewport has attached a camera, so a caller that must
   * REPORT what happened can — the same reason
   * {@link isSceneViewCameraAttached} exists.
   *
   * ⚠ It also RE-BASELINES {@link ISceneModel} 's "has the user moved the
   * camera?" test until the next scene change or camera move. Without that, a
   * step that flew the camera would make the NEXT scene change read as
   * user-moved (the camera is no longer where the outgoing scene's view put it)
   * and silently skip framing the incoming scene — so the learner would land in
   * a new scene at the previous step's pose. The baseline is what keeps the
   * guard meaning "the user moved it", which is the only thing it was ever for.
   */
  applyTransientCameraView(pose: SceneCameraPose, durationSeconds: number): boolean;

  /**
   * Snap the camera to the active scene's view (or the camera default when
   * the scene has none). Port of `DesktopSceneModel.ResetCamera()` (`:206`),
   * which is what the desktop client's footer reset button and its keyboard
   * shortcut both call.
   */
  resetSceneCamera(): void;

  /**
   * Same destination, animated over the ACTIVE scene's transition speed. Port
   * of `DesktopSceneModel.TransitionResetCamera()` (`:218`) — the desktop
   * client's `reset_scene_view` agent tool routes here.
   */
  transitionResetSceneCamera(): void;

  /**
   * {@link transitionResetSceneCamera}, unless the camera is already at the
   * active scene's view or already flying there — then nothing, so a caller
   * that frames right after a scene change does not restart the scene
   * change's own move from the middle.
   *
   * For a guided run's step that has no `step_view`: every step opens on a
   * view, and a scene change alone does not guarantee one (it skips a camera
   * the user moved). Web-only; Unity has no per-step framing.
   *
   * Returns false when no viewport has attached a camera.
   */
  frameActiveSceneView(): boolean;
}

export const SCENE_MODEL = Symbol.for("Campfire.SceneModel");
